import React, { useEffect, useMemo, useState } from "react";
import { FaRegEdit, FaRegTrashAlt } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import axios from "axios";
import apiKey from "../../../config/apiKey";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

/* ---------- Small UI helpers ---------- */
const SkeletonRow = () => (
  <tr className="animate-pulse">
    {[...Array(3)].map((_, i) => (
      <td key={i} className="py-4 px-2">
        <div className="h-4 w-32 bg-gray-200 rounded" />
      </td>
    ))}
  </tr>
);

const AcademicSkill = () => {
  const { t } = useTranslation();
  const admin = JSON.parse(localStorage.getItem("edo-admin"));

  /* ---------- Create (Add skill) ---------- */
  const [newSkillName, setNewSkillName] = useState("");
  const [formError, setFormError] = useState("");

  const addSkill = () => {
    if (!newSkillName.trim()) {
      setFormError(t("adminDash.academicSkill.nameRequired"));
      return;
    }
    setFormError("");

    axios
      .post(
        `${apiKey}/api/edolutions/admin/skills`,
        { skill_name: newSkillName },
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      )
      .then((response) => {
        toast.success(response.data?.message || t("adminDash.academicSkill.addedSuccess"), {
          position: "bottom-right",
          autoClose: 4000,
          theme: "colored",
          style: { backgroundColor: "#2A3A54", color: "#fff" },
        });
        setNewSkillName("");
        fetchSkills();
      })
      .catch((error) => {
        console.error("Error adding skill:", error);
        toast.error(t("adminDash.academicSkill.addError"), {
          position: "bottom-right",
          autoClose: 5000,
          theme: "colored",
        });
      });
  };

  /* ---------- List (GET) ---------- */
  const [skills, setSkills] = useState([]);
  const [fetchError, setFetchError] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchSkills = () => {
    setLoading(true);
    setFetchError("");
    axios
      .get(`${apiKey}/api/edolutions/admin/skills`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      })
      .then((res) => setSkills(res.data || []))
      .catch((error) => setFetchError(error.message || t("adminDash.academicSkill.loadError")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.token]);

  /* ---------- Edit (PUT) ---------- */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSkill, setCurrentSkill] = useState({ id: null, skill_name: "" });

  const openEdit = (id, skill_name) => {
    setCurrentSkill({ id, skill_name });
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentSkill({ id: null, skill_name: "" });
  };

  const updateSkill = () => {
    axios
      .put(
        `${apiKey}/api/edolutions/admin/skills/${currentSkill.id}`,
        { skill_name: currentSkill.skill_name },
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      )
      .then((response) => {
        toast.success(response.data?.message || t("adminDash.academicSkill.updatedSuccess"), {
          position: "bottom-right",
          autoClose: 4000,
          theme: "colored",
          style: { backgroundColor: "#2A3A54", color: "#fff" },
        });
        closeModal();
        fetchSkills();
      })
      .catch((error) => {
        console.error("Error updating skill:", error);
        toast.error(t("adminDash.academicSkill.updateError"), {
          position: "bottom-right",
          autoClose: 5000,
          theme: "colored",
        });
      });
  };

  /* ---------- Delete ---------- */
  const deleteSkill = (id) => {
    axios
      .delete(`${apiKey}/api/edolutions/admin/skills/${id}`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      })
      .then((response) => {
        setSkills((prev) => prev.filter((s) => s.id !== id));
        toast.success(response.data?.message || t("adminDash.academicSkill.removedSuccess"), {
          position: "bottom-right",
          autoClose: 4000,
          theme: "colored",
          style: { backgroundColor: "#2A3A54", color: "#fff" },
        });
      })
      .catch((error) => {
        console.error("Error deleting skill:", error);
        toast.error(t("adminDash.academicSkill.deleteError"), {
          position: "bottom-right",
          autoClose: 5000,
          theme: "colored",
        });
      });
  };

  /* ---------- Search (debounced) ---------- */
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filtered = useMemo(() => {
    const term = debouncedSearch.toLowerCase();
    if (!term) return skills;
    return skills.filter((item) => (item.skill_name || "").toLowerCase().includes(term));
  }, [skills, debouncedSearch]);

  /* ---------- Pagination ---------- */
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = Math.min((currentPage - 1) * itemsPerPage, totalItems);
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const pageSlice = useMemo(() => filtered.slice(startIndex, endIndex), [filtered, startIndex, endIndex]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, debouncedSearch]);

  // Windowed page numbers (max 5)
  const pageRange = useMemo(() => {
    const maxBtns = 5;
    if (totalPages <= maxBtns) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - (maxBtns - 1)));
    return Array.from({ length: maxBtns }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  return (
    <div className="relative my-8 max-sm:max-w-xs mx-auto sm:w-full flex flex-col min-w-0 mb-6 shadow-sm bg-white rounded-2xl border border-gray-200">
      {/* Add Skill */}
      <div className="px-6 pt-6">
        <p className="text-xl font-bold">{t("adminDash.academicSkill.addTitle")}</p>
        <div className="mt-4 mb-2 flex max-sm:flex-col max-sm:items-stretch gap-2">
          <input
            type="text"
            className="border p-2 shadow-sm w-full sm:w-80 rounded-md outline-none"
            placeholder={t("adminDash.academicSkill.namePlaceholder")}
            value={newSkillName}
            onChange={(e) => {
              setNewSkillName(e.target.value);
              setFormError("");
            }}
          />
          <button
            onClick={addSkill}
            className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:from-[#2A3A54] hover:to-[#EE5F2B] text-sm font-semibold rounded-md text-white px-5 py-2"
          >
            {t("adminDash.common.submit")}
          </button>
        </div>
        {(formError || fetchError) && (
          <p className="text-red-600 text-sm mb-2">{formError || fetchError}</p>
        )}
      </div>

      {/* Toolbar */}
      <div className="px-6 py-4 flex flex-wrap items-center justify-between">
        <p className="text-lg font-semibold">{t("adminDash.academicSkill.listTitle")}</p>
        <div className="relative border rounded-md flex items-center px-4 py-2 shadow-sm">
          <FiSearch className="h-5 w-5 absolute left-2 text-gray-500" />
          <input
            type="text"
            className="outline-none pl-7 text-sm"
            placeholder={t("adminDash.academicSkill.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="mx-6 mb-6 overflow-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-50 text-[#6b7280] sticky top-0 z-10">
            <tr>
              <th className="py-3 px-2 font-medium w-1/12">{t("adminDash.common.sr")}</th>
              <th className="py-3 px-2 font-medium">{t("adminDash.academicSkill.colName")}</th>
              <th className="py-3 px-2 font-medium text-center w-2/12">{t("adminDash.common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y text-center">
            {loading && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}

            {!loading && pageSlice.length === 0 && (
              <tr>
                <td colSpan={3} className="py-8 px-2 text-gray-500">
                  {t("adminDash.academicSkill.noData")}
                </td>
              </tr>
            )}

            {!loading &&
              pageSlice.map((item, idx) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="py-4 px-2">{startIndex + idx + 1}</td>
                  <td className="py-4 px-2 text-left">{item.skill_name}</td>
                  <td className="py-4 px-2">
                    <div className="flex justify-center gap-6">
                      <button
                        onClick={() => openEdit(item.id, item.skill_name)}
                        className="text-[#2A3A54] hover:opacity-80"
                        title={t("adminDash.common.edit")}
                      >
                        <FaRegEdit className="text-lg" />
                      </button>
                      <button
                        onClick={() => deleteSkill(item.id)}
                        className="text-rose-600 hover:text-rose-700"
                        title={t("adminDash.common.delete")}
                      >
                        <FaRegTrashAlt className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap gap-5 justify-between items-center w-full text-sm px-6 pb-6">
        <div className="flex items-center gap-2 text-gray-600">
          <select
            className="text-sm border rounded-lg px-2 py-1 bg-white"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}
          >
            {[10, 20, 30, 40, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>{t("adminDash.common.showing")}</span>
          <span className="font-medium text-gray-800">
            {totalItems === 0 ? 0 : startIndex + 1}-{endIndex}
          </span>
          <span>{t("adminDash.common.of")} {totalItems} {t("adminDash.common.entries")}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <button
            className="text-base bg-gray-100 rounded-lg px-2 py-1 disabled:opacity-40"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label={t("adminDash.common.previousPage")}
          >
            <MdKeyboardArrowLeft />
          </button>

          {pageRange.map((p) => (
            <button
              key={p}
              className={`text-base rounded-lg px-3 py-1 ${
                p === currentPage ? "text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]" : "text-black bg-gray-100"
              }`}
              onClick={() => setCurrentPage(p)}
            >
              {p}
            </button>
          ))}

          <span>{t("adminDash.common.of")} {Math.max(1, totalPages)} {t("adminDash.common.pages")}</span>

          <button
            className="text-base bg-gray-100 rounded-lg px-2 py-1 disabled:opacity-40"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            aria-label={t("adminDash.common.nextPage")}
          >
            <MdKeyboardArrowRight />
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="px-5 py-4 border-b">
              <h2 className="text-lg font-semibold">{t("adminDash.academicSkill.updateTitle")}</h2>
            </div>
            <div className="px-5 py-5">
              <input
                type="text"
                className="border p-2 w-full rounded-md shadow-sm"
                value={currentSkill.skill_name}
                onChange={(e) =>
                  setCurrentSkill({ ...currentSkill, skill_name: e.target.value })
                }
                placeholder={t("adminDash.academicSkill.namePlaceholderShort")}
              />
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                onClick={closeModal}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm"
              >
                {t("adminDash.common.cancel")}
              </button>
              <button
                onClick={updateSkill}
                className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:from-[#2A3A54] hover:to-[#EE5F2B] hover:opacity-90 text-white px-4 py-2 rounded-lg text-sm"
              >
                {t("adminDash.common.update")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicSkill;
